import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Children } from "../../../../../../../../components/miscellianous/children"
import { BSBuilding } from "../../../../../../../../core/dto/beem-shot/BSBuilding/BSBuilding"
import { BSBuildingCreation } from "../../../../../../../../core/dto/beem-shot/BSBuilding/BSBuildingCreation"
import { BSBuildingUpdateDto } from "../../../../../../../../core/dto/beem-shot/BSBuilding/BSBuildingUpdateDto"
import { CalculStatusEnum } from "../../../../../../../../core/enum/calculStatusEnum"
import { useBSBuilding } from "../../../../../../../../core/hooks/beem-shot/useBSBuilding"
import { BSBuildingListContext } from "./BSBuildingListContext"
import { BSVariantContext } from "../../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"

export const BSBuildingContext = React.createContext<BSBuildingStore>({} as BSBuildingStore)

export function BSBuildingContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { bsBuildingId } = useParams()
  const [searchParams] = useSearchParams()

  const { createBuilding, putBuildingInfos, getStartEstimating, sendDeleteBuilding } = useBSBuilding()

  const { selectedVariant } = useContext(BSVariantContext)
  const { bsBuildingList, setBSBuildingList, updateOneBSBuildingStatusState, refreshBSBuildingList } =
    useContext(BSBuildingListContext)

  const [selectedBSBuilding, setSelectedBSBuilding] = useState<BSBuilding | undefined>()

  const createNewBSBuilding: (bsBuildingCreation: BSBuildingCreation) => Promise<BSBuilding> = useCallback(
    (bsBuildingCreation) =>
      createBuilding(bsBuildingCreation).then((newBSBuilding) => {
        setBSBuildingList((prev) => [...prev, newBSBuilding])
        setSelectedBSBuilding(newBSBuilding)
        return newBSBuilding
      }),
    [createBuilding, setBSBuildingList]
  )

  const selectBSBuilding: (buildingId: string) => void = useCallback(
    (buildingId: string) => {
      const buildingFound = bsBuildingList.find((bsBuilding) => bsBuilding.id === buildingId)
      if (buildingFound) {
        setSelectedBSBuilding(buildingFound)
      } else {
        setSelectedBSBuilding(undefined)
      }
    },
    [bsBuildingList]
  )

  useEffect(() => {
    const bsBuildingIdParams = searchParams.get("bsBuildingId")
    if (bsBuildingId) {
      selectBSBuilding(bsBuildingId)
    } else if (bsBuildingIdParams) {
      selectBSBuilding(bsBuildingIdParams)
    } else {
      selectBSBuilding("")
    }
  }, [bsBuildingId, bsBuildingList, searchParams, selectBSBuilding])

  const updateBSBuildingInfos: (bsBuildingUpdateDto: BSBuildingUpdateDto) => Promise<any> = useCallback(
    (bsBuildingUpdateDto) =>
      putBuildingInfos(bsBuildingUpdateDto).then((updatedBSBuilding) => {
        setBSBuildingList((prev) =>
          prev.map((bsBuilding) => (bsBuilding.id === updatedBSBuilding.id ? updatedBSBuilding : bsBuilding))
        )
        setSelectedBSBuilding(updatedBSBuilding)
        return updateBSBuildingInfos
      }),
    [putBuildingInfos, setBSBuildingList]
  )

  const updateCalculStatusStateById: (buildingId: string, status: CalculStatusEnum) => void = useCallback(
    (buildingId, status) => {
      updateOneBSBuildingStatusState(buildingId, status)

      setSelectedBSBuilding((prev): BSBuilding | undefined => {
        if (prev) {
          return { ...prev, calculStatus: status }
        } else {
          return prev
        }
      })
    },
    [updateOneBSBuildingStatusState]
  )

  const updateCalculStatusState: (status: CalculStatusEnum) => void = useCallback(
    (status) => {
      if (selectedBSBuilding?.id) {
        updateCalculStatusStateById(selectedBSBuilding.id, status)
      }
    },
    [selectedBSBuilding?.id, updateCalculStatusStateById]
  )

  const updateBSBuildingModelId: (newBSBimModelId: string) => void = useCallback((newBSBimModelId: string) => {
    setSelectedBSBuilding((prev): BSBuilding | undefined => {
      if (prev) {
        return { ...prev, bsBimModelId: newBSBimModelId }
      } else {
        return prev
      }
    })
  }, [])

  const deleteBuilding: (buildingId: string) => Promise<any> = useCallback(
    (buildingId) =>
      sendDeleteBuilding(buildingId).then(() => {
        setSelectedBSBuilding(undefined)
        setBSBuildingList((prev: BSBuilding[]) => prev.filter((building) => building.id !== buildingId))
      }),
    [sendDeleteBuilding, setBSBuildingList]
  )

  const startEstimating: (buildingId: string) => Promise<any> = useCallback(
    (buildingId) => {
      let targetId
      if (buildingId) {
        targetId = buildingId
      } else if (selectedBSBuilding?.id) {
        targetId = selectedBSBuilding.id
      }

      if (targetId) {
        updateCalculStatusStateById(targetId, CalculStatusEnum.WAITING_FOR_CONFIGURATION)
        return getStartEstimating(targetId).then(() => {
          if (selectedVariant?.id) {
            return refreshBSBuildingList(selectedVariant?.id)
          } else {
            return Promise.resolve()
          }
        })
      } else {
        return Promise.resolve()
      }
    },
    [getStartEstimating, refreshBSBuildingList, selectedBSBuilding?.id, selectedVariant?.id, updateCalculStatusStateById]
  )

  const bsBuildingStore = useMemo(
    () => ({
      selectedBSBuilding,
      createNewBSBuilding,
      updateBSBuildingInfos,
      updateBSBuildingModelId,
      updateCalculStatusState,
      deleteBuilding,
      startEstimating,
      selectBSBuilding,
    }),
    [
      createNewBSBuilding,
      selectedBSBuilding,
      updateBSBuildingInfos,
      updateBSBuildingModelId,
      updateCalculStatusState,
      deleteBuilding,
      startEstimating,
      selectBSBuilding,
    ]
  )
  return <BSBuildingContext.Provider value={bsBuildingStore}>{children}</BSBuildingContext.Provider>
}

export interface BSBuildingStore {
  selectedBSBuilding: BSBuilding | undefined
  createNewBSBuilding(bsBuildingCreation: BSBuildingCreation): Promise<BSBuilding>
  updateBSBuildingInfos(bsBuildingUpdateDto: BSBuildingUpdateDto): Promise<BSBuilding>
  updateBSBuildingModelId(newBsBimModelId: string): void
  updateCalculStatusState(status: CalculStatusEnum): void
  deleteBuilding(buildingId: string): Promise<any>
  startEstimating(buildingId: string): Promise<any>
  selectBSBuilding(buildingId: string): void
}
